<template>
  <div>
    <div class="d-flex justify-space-between align-center mb-3">
      <h3>{{ title }}</h3>
      <v-btn
        color="primary"
        outlined
        small
        @click="downloadCSV"
        :disabled="!data || data.length === 0"
      >
        <v-icon left small>mdi-download</v-icon>
        Download CSV
      </v-btn>
    </div>
    <div class="mt-4 mb-8 text-caption text--secondary">
      <div v-if="currentUpperLimit !== undefined || currentLowerLimit !== undefined">
        <strong>Violation limits:</strong>
        <span v-if="currentUpperLimit !== undefined">
          Upper limit:
          <v-text-field
            v-model.number="currentUpperLimit"
            type="number"
            step="0.01"
            dense
            outlined
            hide-details
            style="width: 80px; display: inline-block; margin: 0 4px"
          ></v-text-field>
          {{ units }}
        </span>
        <span v-if="currentUpperLimit !== undefined && currentLowerLimit !== undefined"> • </span>
        <span v-if="currentLowerLimit !== undefined">
          Lower limit:
          <v-text-field
            v-model.number="currentLowerLimit"
            type="number"
            step="0.01"
            dense
            outlined
            hide-details
            style="width: 80px; display: inline-block; margin: 0 4px"
          ></v-text-field>
          {{ units }}
        </span>
        <v-tooltip top>
          <template #activator="{ on }">
            <v-icon color="warning" icon v-on="on">mdi-alert-circle</v-icon>
          </template>
          <span
            >Note: Changing limits here won't affect contingency analysis results. To do that,
            change in case settings and re-run the case.</span
          >
        </v-tooltip>
      </div>
      <div class="mt-1">
        Values in <v-chip color="deep-orange" dark x-small>orange</v-chip> exceed the violation
        limits.
      </div>
    </div>

    <v-data-table :headers="headers" :items="sortedData" v-bind="$attrs" v-on="$listeners">
      <template v-for="column in dataColumns" v-slot:[`item.${column.key}`]="{ item }">
        <v-chip
          v-if="isViolation(item[column.key])"
          color="deep-orange"
          dark
          small
          :key="column.key"
        >
          {{ formatNumber(item[column.key]) }}
        </v-chip>
        <span v-else :key="column.key">
          {{ formatNumber(item[column.key]) }}
        </span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import Papa from "papaparse";
import download from "downloadjs";

export default {
  name: "PFViolationReport",
  props: {
    data: { type: Array, required: true },
    upperLimit: { type: Number, required: false },
    lowerLimit: { type: Number, required: false },
    units: { type: String, required: true },
    title: { type: String, required: true },
  },
  data() {
    return {
      currentUpperLimit: this.upperLimit,
      currentLowerLimit: this.lowerLimit,
    };
  },
  watch: {
    upperLimit(newVal) {
      this.currentUpperLimit = newVal;
    },
    lowerLimit(newVal) {
      this.currentLowerLimit = newVal;
    },
  },
  computed: {
    headers() {
      if (!this.data || this.data.length === 0) return [];

      const firstRow = this.data[0];
      return Object.keys(firstRow).map((key) => ({
        text: key,
        value: key,
      }));
    },
    dataColumns() {
      if (!this.data || this.data.length === 0) return [];

      const firstRow = this.data[0];
      return Object.keys(firstRow)
        .filter((key) => typeof firstRow[key] === "number")
        .map((key) => ({ key }));
    },
    sortedData() {
      if (!this.data || this.data.length === 0) return [];

      return [...this.data].sort((a, b) => {
        const aHasViolation = this.rowHasViolation(a);
        const bHasViolation = this.rowHasViolation(b);

        if (aHasViolation && !bHasViolation) return -1;
        if (!aHasViolation && bHasViolation) return 1;
        return 0;
      });
    },
  },
  methods: {
    isViolation(value) {
      if (typeof value !== "number") return false;

      const hasUpperViolation =
        this.currentUpperLimit !== undefined && value > this.currentUpperLimit;
      const hasLowerViolation =
        this.currentLowerLimit !== undefined && value < this.currentLowerLimit;

      return hasUpperViolation || hasLowerViolation;
    },
    formatNumber(value) {
      if (typeof value !== "number") return value;
      return Number(value.toFixed(3));
    },
    rowHasViolation(row) {
      return this.dataColumns.some((column) => this.isViolation(row[column.key]));
    },
    downloadCSV() {
      if (!this.data || this.data.length === 0) return;

      // Use sortedData to maintain violation-first ordering
      const csvData = Papa.unparse(this.sortedData, {
        newline: "\n",
        header: true,
      });

      // Create filename from title
      const filename = `${this.title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.csv`;

      download(csvData, filename, "text/csv");
    },
  },
};
</script>

<style>
th {
  vertical-align: top;
}
</style>
